(() => {
  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(cents / 100);
    } catch (_error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const initMobileMenus = (scope = document) => {
    scope.querySelectorAll('[data-mobile-menu]').forEach((menu) => {
      if (menu.dataset.initialized === 'true') return;
      menu.dataset.initialized = 'true';

      const summary = menu.querySelector('summary');
      const syncState = () => summary?.setAttribute('aria-expanded', String(menu.open));
      syncState();
      menu.addEventListener('toggle', syncState);

      menu.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => menu.removeAttribute('open'));
      });

      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          menu.removeAttribute('open');
          summary?.focus();
        }
      });
    });
  };

  const initAutoSubmit = (scope = document) => {
    scope.querySelectorAll('[data-auto-submit]').forEach((field) => {
      if (field.dataset.initialized === 'true') return;
      field.dataset.initialized = 'true';
      field.addEventListener('change', () => field.form?.submit());
    });
  };

  const initProduct = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const variantsNode = root.querySelector('[data-product-variants]');
    const form = root.querySelector('[data-product-form]');
    if (!variantsNode || !form) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsNode.textContent);
    } catch (_error) {
      return;
    }

    const selectors = Array.from(form.querySelectorAll('[data-option-position]'));
    const variantInput = form.querySelector('[data-variant-id]');
    const addButton = form.querySelector('[data-add-to-cart]');
    const addText = form.querySelector('[data-add-to-cart-text]');
    const status = form.querySelector('[data-variant-status]');
    const price = root.querySelector('[data-product-price]');
    const currency = root.dataset.moneyCode;
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const stickyAddButton = root.querySelector('[data-sticky-add-to-cart]');
    const stickyAddText = root.querySelector('[data-sticky-add-to-cart-text]');

    const selectMedia = (mediaId) => {
      if (!mediaId) return;
      const thumbnail = root.querySelector(`[data-media-target="${mediaId}"]`);
      thumbnail?.click();
    };

    const updateVariant = () => {
      const selectedOptions = selectors.map((selector) => selector.value);
      const variant = variants.find((item) => item.options.every((option, index) => option === selectedOptions[index]));

    if (!variant) {
      if (variantInput) variantInput.value = '';
      if (addButton) addButton.disabled = true;
      if (addText) addText.textContent = 'Signal unavailable';
      if (status) status.textContent = 'This combination is unavailable.';
      if (stickyAddButton) stickyAddButton.disabled = true;
      if (stickyAddText) stickyAddText.textContent = 'Signal unavailable';
      return;
    }

    if (variantInput) variantInput.value = variant.id;
    if (addButton) addButton.disabled = !variant.available;
    if (addText) addText.textContent = variant.available ? 'Add to cart' : 'Signal unavailable';
    if (status) status.textContent = variant.available ? 'Selected signal is ready.' : 'Selected signal is unavailable.';
    if (price) price.textContent = formatMoney(variant.price, currency);
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, currency);
    if (stickyAddButton) stickyAddButton.disabled = !variant.available;
    if (stickyAddText) stickyAddText.textContent = variant.available ? 'Add to cart' : 'Signal unavailable';
    selectMedia(variant.featured_media?.id);

      const nextUrl = new URL(root.dataset.productUrl, window.location.origin);
      nextUrl.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}`);
    };

    selectors.forEach((selector) => selector.addEventListener('change', updateVariant));

    const featuredImage = root.querySelector('.product-gallery__featured');
    root.querySelectorAll('[data-media-target]').forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        if (!featuredImage) return;
        featuredImage.src = thumbnail.dataset.mediaSrc;
        if (thumbnail.dataset.mediaSrcset) featuredImage.srcset = thumbnail.dataset.mediaSrcset;
        featuredImage.alt = thumbnail.dataset.mediaAlt || '';
        featuredImage.dataset.mediaId = thumbnail.dataset.mediaTarget;
        root.querySelectorAll('[data-media-target]').forEach((item) => {
          const active = item === thumbnail;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
      });
    });
  };

  const initAccessibleTables = (scope = document) => {
    scope.querySelectorAll('.product-description table').forEach((table, tableIndex) => {
      if (table.dataset.accessible === 'true') return;
      table.dataset.accessible = 'true';

      table.querySelectorAll('tr:first-child th').forEach((header, headerIndex) => {
        header.setAttribute('scope', 'col');
        if (!header.textContent.trim()) {
          const label = document.createElement('span');
          label.className = 'visually-hidden';
          label.textContent = headerIndex === 0 ? 'Measurement' : `Column ${headerIndex + 1}`;
          header.append(label);
        }
      });

      table.querySelectorAll('tr').forEach((row, rowIndex) => {
        if (rowIndex === 0) return;
        const firstCell = row.querySelector('th, td');
        if (firstCell?.tagName === 'TH') firstCell.setAttribute('scope', 'row');
      });

      if (!table.parentElement?.classList.contains('table-scroll')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'table-scroll';
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', `Product size information ${tableIndex + 1}`);
        wrapper.tabIndex = 0;
        table.before(wrapper);
        wrapper.append(table);
      }
    });
  };

  const initPolicyPresentation = (scope = document) => {
    const policy = scope.querySelector('.shopify-policy__container');
    if (!policy || policy.dataset.initialized === 'true') return;
    policy.dataset.initialized = 'true';

    const configNode = document.querySelector('[data-theme-config]');
    let shopName = 'Pixel Threads';
    try {
      shopName = JSON.parse(configNode?.textContent || '{}').shopName || shopName;
    } catch (_error) {}

    const walker = document.createTreeWalker(policy, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      node.textContent = node.textContent
        .replace(/Pixle Threads/g, shopName)
        .replace(/All About Coffee/g, shopName)
        .replace(
          /\[INSERT RETURN ADDRESS\]/g,
          'Contact us before sending a return. We will provide the current return address with your return authorization.',
        );
    });
  };

  const initRecommendations = (scope = document) => {
    scope.querySelectorAll('product-recommendations').forEach((element) => {
      if (element.dataset.initialized === 'true' || !element.dataset.url) return;
      element.dataset.initialized = 'true';

      fetch(element.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const recommendations = html.querySelector('product-recommendations');
          if (recommendations?.innerHTML.trim()) element.innerHTML = recommendations.innerHTML;
        })
        .catch(() => {});
    });
  };

  const initialize = (scope = document) => {
    initMobileMenus(scope);
    initAutoSubmit(scope);
    scope.querySelectorAll('[data-product-root]').forEach(initProduct);
    initAccessibleTables(scope);
    initPolicyPresentation(scope);
    initRecommendations(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
